import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserCodeReader, BrowserQRCodeReader } from "@zxing/browser";
import {
  FiArrowLeft,
  FiBell,
  FiCamera,
  FiCheck,
  FiCheckCircle,
  FiClipboard,
  FiClock,
  FiCreditCard,
  FiEye,
  FiKey,
  FiMaximize,
  FiRefreshCw,
  FiUser,
  FiVolume2,
  FiVolumeX,
  FiX,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import {
  buildDeviceAuthHeaders,
  completeDeviceCredential,
  createDeviceCredential,
} from "./deviceCredential";
import "./serviceDisplay.css";

const DEFAULT_ALERT_SETTINGS = {
  sound_enabled: true,
  visual_enabled: true,
  tone: "chime",
  volume: 80,
  repeat_seconds: 30,
};

function formatMoney(value) {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(value || 0));
}

function formatTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-ES", { hour: "2-digit", minute: "2-digit" }).format(date);
}

function elapsedLabel(value, now) {
  const submittedAt = new Date(value).getTime();
  if (!Number.isFinite(submittedAt)) return "";
  const minutes = Math.max(0, Math.floor((now - submittedAt) / 60000));
  if (minutes < 1) return "Just now";
  if (minutes === 1) return "1 min";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
}

function orderPaymentLabel(order) {
  const method = order.payment_method_label || "Payment not specified";
  if (order.payment_method_category === "room_charge" && order.room_number) {
    return `${method} · Room ${order.room_number}`;
  }
  return method;
}

function alertToneNotes(tone) {
  return tone === "urgent"
    ? [
      { frequency: 740, offset: 0, duration: 0.16, waveform: "square" },
      { frequency: 740, offset: 0.24, duration: 0.16, waveform: "square" },
      { frequency: 920, offset: 0.48, duration: 0.22, waveform: "square" },
    ]
    : tone === "bell"
      ? [
        { frequency: 660, offset: 0, duration: 0.5, waveform: "sine" },
        { frequency: 990, offset: 0.08, duration: 0.62, waveform: "sine" },
      ]
      : [
        { frequency: 880, offset: 0, duration: 0.24, waveform: "sine" },
        { frequency: 1175, offset: 0.22, duration: 0.34, waveform: "sine" },
      ];
}

function writeWaveText(view, offset, text) {
  for (let index = 0; index < text.length; index += 1) {
    view.setUint8(offset + index, text.charCodeAt(index));
  }
}

function createAlertAudio(tone) {
  const normalizedTone = ["chime", "bell", "urgent"].includes(tone) ? tone : "chime";
  const notes = alertToneNotes(normalizedTone);
  const sampleRate = 44100;
  const duration = Math.max(...notes.map((note) => note.offset + note.duration)) + 0.08;
  const sampleCount = Math.ceil(sampleRate * duration);
  const buffer = new ArrayBuffer(44 + sampleCount * 2);
  const view = new DataView(buffer);

  writeWaveText(view, 0, "RIFF");
  view.setUint32(4, 36 + sampleCount * 2, true);
  writeWaveText(view, 8, "WAVE");
  writeWaveText(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeWaveText(view, 36, "data");
  view.setUint32(40, sampleCount * 2, true);

  for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex += 1) {
    const time = sampleIndex / sampleRate;
    let sample = 0;
    notes.forEach((note) => {
      const localTime = time - note.offset;
      if (localTime < 0 || localTime > note.duration) return;
      const attack = Math.min(1, localTime / 0.012);
      const release = Math.min(1, (note.duration - localTime) / 0.06);
      const envelope = Math.max(0, Math.min(attack, release));
      const sine = Math.sin(2 * Math.PI * note.frequency * localTime);
      const wave = note.waveform === "square" ? Math.sign(sine) * 0.7 : sine;
      sample += wave * envelope * (normalizedTone === "urgent" ? 0.34 : 0.3);
    });
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(44 + sampleIndex * 2, Math.round(clamped * 32767), true);
  }

  const url = URL.createObjectURL(new Blob([buffer], { type: "audio/wav" }));
  const audio = new Audio(url);
  audio.preload = "auto";
  audio.setAttribute("playsinline", "");
  return { audio, tone: normalizedTone, url };
}

function disposeAlertAudio(entry) {
  if (!entry) return;
  entry.audio.pause();
  entry.audio.removeAttribute("src");
  entry.audio.load();
  URL.revokeObjectURL(entry.url);
}

function createIdempotencyKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "_");
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function extractServiceDisplayPairingToken(value) {
  const raw = String(value || "").trim();
  if (/^[A-Za-z0-9_-]{32,128}$/.test(raw)) return raw;

  try {
    const parsed = new URL(raw, window.location.origin);
    const match = parsed.pathname.match(/^\/pos\/service-display\/([A-Za-z0-9_-]{32,128})\/?$/);
    if (match) return match[1];
  } catch (_err) {
    // Fall through to the user-facing validation error.
  }

  throw new Error("Scan a Service Display pairing QR generated in POS Setup.");
}

function ServiceDisplayPairing({ initialError, onClearError, onPair }) {
  const videoRef = useRef(null);
  const scannerControlsRef = useRef(null);
  const pairingRef = useRef(false);
  const [stage, setStage] = useState("idle");
  const [manualValue, setManualValue] = useState("");
  const [error, setError] = useState("");

  const stopScanner = useCallback(() => {
    try { scannerControlsRef.current?.stop(); } catch (_err) {}
    scannerControlsRef.current = null;
    BrowserCodeReader.releaseAllStreams();
  }, []);

  useEffect(() => () => stopScanner(), [stopScanner]);

  const submitPairing = useCallback(async (rawValue) => {
    if (pairingRef.current) return;
    pairingRef.current = true;
    stopScanner();
    try {
      const pairingToken = extractServiceDisplayPairingToken(rawValue);
      setStage("pairing");
      setError("");
      onClearError();
      await onPair(pairingToken);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not pair this Service Display.");
      setStage("idle");
    } finally {
      pairingRef.current = false;
    }
  }, [onClearError, onPair, stopScanner]);

  const startScanner = async () => {
    try {
      stopScanner();
      setError("");
      onClearError();
      setStage("scanning");
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      const reader = new BrowserQRCodeReader(undefined, { delayBetweenScanAttempts: 200 });
      const controls = await reader.decodeFromConstraints(
        { video: { facingMode: { ideal: "environment" } }, audio: false },
        videoRef.current,
        (scanResult, _scanError, activeControls) => {
          if (!scanResult || pairingRef.current) return;
          activeControls.stop();
          void submitPairing(scanResult.getText());
        }
      );
      scannerControlsRef.current = controls;
    } catch (err) {
      stopScanner();
      setStage("idle");
      setError(
        err?.name === "NotAllowedError"
          ? "Camera access was denied. Allow camera access for Service Display and try again."
          : "Could not start the camera. Paste the pairing link below instead."
      );
    }
  };

  const cancelScanner = () => {
    stopScanner();
    setStage("idle");
    setError("");
  };

  return (
    <main className="serviceDisplayPairing">
      <section className="serviceDisplayPairingPanel">
        {stage === "scanning" ? (
          <div className="serviceDisplayScanner serviceDisplayPairingScanner">
            <div className="serviceDisplayScannerViewport"><video ref={videoRef} autoPlay muted playsInline /><span className="serviceDisplayScannerFrame" /></div>
            <div className="serviceDisplayScannerFooter"><span>Scan the pairing QR shown in POS Setup</span><button type="button" onClick={cancelScanner}><FiX /> Cancel</button></div>
          </div>
        ) : (
          <>
            <span className={`serviceDisplayPairingIcon ${stage === "pairing" ? "serviceDisplayPairingIcon--spinning" : ""}`}>{stage === "pairing" ? <FiRefreshCw /> : <FiKey />}</span>
            <h1>{stage === "pairing" ? "Pairing Service Display" : "Pair this Service Display"}</h1>
            <p>Generate a fresh pairing QR in POS Setup and scan it from this installed screen.</p>
            {error || initialError ? <div className="serviceDisplayPairingError"><FiX /> {error || initialError}</div> : null}
            <button type="button" className="serviceDisplayPairingPrimary" onClick={startScanner} disabled={stage === "pairing"}>
              <FiCamera /> Open camera
            </button>
            <div className="serviceDisplayPairingManual">
              <input
                value={manualValue}
                onChange={(event) => setManualValue(event.target.value)}
                placeholder="Pairing link or code"
                disabled={stage === "pairing"}
              />
              <button type="button" onClick={() => submitPairing(manualValue)} disabled={stage === "pairing" || !manualValue.trim()}>
                <FiCheck /> Pair
              </button>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

function ServiceDisplayCredit({ pendingOrderCount }) {
  const videoRef = useRef(null);
  const scannerControlsRef = useRef(null);
  const resolvingRef = useRef(false);
  const redemptionKeyRef = useRef(createIdempotencyKey());
  const [stage, setStage] = useState("idle");
  const [credential, setCredential] = useState("");
  const [manualCredential, setManualCredential] = useState("");
  const [account, setAccount] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const stopScanner = useCallback(() => {
    try { scannerControlsRef.current?.stop(); } catch (_err) {}
    scannerControlsRef.current = null;
    BrowserCodeReader.releaseAllStreams();
  }, []);

  useEffect(() => () => stopScanner(), [stopScanner]);

  const resolveCredential = useCallback(async (rawCredential) => {
    if (resolvingRef.current) return;
    resolvingRef.current = true;
    stopScanner();
    try {
      setStage("resolving");
      setError("");
      const payload = { qr_credential: String(rawCredential || "").trim() };
      const headers = await buildDeviceAuthHeaders("POST", payload);
      const res = await axios.post("/api/pos/service-display/customer-credit/resolve", payload, { headers, withCredentials: true });
      setCredential(payload.qr_credential);
      redemptionKeyRef.current = createIdempotencyKey();
      setAccount(res.data?.account || null);
      setAmount("");
      setNote("");
      setStage("account");
    } catch (err) {
      setAccount(null);
      setStage("idle");
      setError(err?.response?.data?.error || "Could not read this customer credit QR.");
    } finally {
      resolvingRef.current = false;
    }
  }, [stopScanner]);

  const startScanner = async () => {
    try {
      stopScanner();
      setError("");
      setResult(null);
      setAccount(null);
      setStage("scanning");
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      const reader = new BrowserQRCodeReader(undefined, { delayBetweenScanAttempts: 200 });
      const controls = await reader.decodeFromConstraints(
        { video: { facingMode: { ideal: "environment" } }, audio: false },
        videoRef.current,
        (scanResult, _scanError, activeControls) => {
          if (!scanResult || resolvingRef.current) return;
          activeControls.stop();
          void resolveCredential(scanResult.getText());
        }
      );
      scannerControlsRef.current = controls;
    } catch (err) {
      stopScanner();
      setStage("idle");
      setError(
        err?.name === "NotAllowedError"
          ? "Camera access was denied. Allow camera access in the browser and try again."
          : "Could not start the camera. Use the manual code field below."
      );
    }
  };

  const resetCredit = () => {
    stopScanner();
    setStage("idle");
    setCredential("");
    setManualCredential("");
    setAccount(null);
    setAmount("");
    setNote("");
    setError("");
    setResult(null);
    redemptionKeyRef.current = createIdempotencyKey();
  };

  const redeem = async () => {
    try {
      setSaving(true);
      setError("");
      const payload = {
        qr_credential: credential,
        amount,
        note,
        idempotency_key: redemptionKeyRef.current,
      };
      const headers = await buildDeviceAuthHeaders("POST", payload);
      const res = await axios.post("/api/pos/service-display/customer-credit/redeem", payload, { headers, withCredentials: true });
      setResult({ account: res.data?.account, amount: Number(amount || 0) });
      setAccount(res.data?.account || account);
      setStage("success");
    } catch (err) {
      setError(err?.response?.data?.error || "Could not register this consumption.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="serviceDisplayCredit">
      <header className="serviceDisplayCreditHeader">
        <div><h2>Customer credit</h2><span>Scan the customer's live QR and enter the consumed amount.</span></div>
        {pendingOrderCount ? <span className="serviceDisplayCreditOrderNotice"><FiBell /> {pendingOrderCount} pending {pendingOrderCount === 1 ? "order" : "orders"}</span> : null}
      </header>

      {error ? <div className="serviceDisplayCreditError"><FiX /> {error}</div> : null}

      {stage === "idle" ? (
        <section className="serviceDisplayCreditStart">
          <span className="serviceDisplayCreditStartIcon"><FiCamera /></span>
          <h3>Ready to scan</h3>
          <button type="button" onClick={startScanner}><FiCamera /> Open camera</button>
          <div className="serviceDisplayCreditManual">
            <input value={manualCredential} onChange={(event) => setManualCredential(event.target.value)} placeholder="Paste QR code" />
            <button type="button" onClick={() => resolveCredential(manualCredential)} disabled={!manualCredential.trim()}><FiArrowLeft /> Read</button>
          </div>
        </section>
      ) : null}

      {stage === "scanning" ? (
        <section className="serviceDisplayScanner">
          <div className="serviceDisplayScannerViewport"><video ref={videoRef} autoPlay muted playsInline /><span className="serviceDisplayScannerFrame" /></div>
          <div className="serviceDisplayScannerFooter"><span>Point the camera at the customer's QR</span><button type="button" onClick={resetCredit}><FiX /> Cancel</button></div>
        </section>
      ) : null}

      {stage === "resolving" ? <div className="serviceDisplayCreditLoading"><FiRefreshCw /> Reading customer credit</div> : null}

      {stage === "account" && account ? (
        <section className="serviceDisplayCreditAccount">
          <header><span className="serviceDisplayCreditAvatar"><FiUser /></span><div><span>Customer</span><h3>{account.display_name}</h3></div><button type="button" onClick={resetCredit} aria-label="Scan another QR" title="Scan another QR"><FiX /></button></header>
          <div className="serviceDisplayCreditAvailable"><span>Available balance</span><strong>{formatMoney(account.balance_amount)}</strong></div>
          <div className="serviceDisplayCreditRedeemForm">
            <label><span>Amount consumed</span><div><input autoFocus type="number" inputMode="decimal" min="0.01" max={account.balance_amount} step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0,00" /><strong>EUR</strong></div></label>
            <label><span>Note · optional</span><input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Internal reference" /></label>
            <button type="button" onClick={redeem} disabled={saving || !amount || Number(amount) <= 0 || Number(amount) > Number(account.balance_amount)}><FiCreditCard /> {saving ? "Registering" : `Use ${formatMoney(amount)}`}</button>
          </div>
        </section>
      ) : null}

      {stage === "success" && result?.account ? (
        <section className="serviceDisplayCreditSuccess">
          <FiCheckCircle />
          <h3>Consumption registered</h3>
          <strong>{formatMoney(result.amount)}</strong>
          <span>{result.account.display_name}</span>
          <div><small>Remaining balance</small><strong>{formatMoney(result.account.balance_amount)}</strong></div>
          <button type="button" onClick={resetCredit}><FiCamera /> Scan another customer</button>
        </section>
      ) : null}
    </main>
  );
}

export default function ServiceDisplayPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const pairingStartedRef = useRef(false);
  const knownOrderIdsRef = useRef(null);
  const alertAudioRef = useRef(null);
  const wakeLockRef = useRef(null);
  const soundReadyRef = useRef(false);
  const soundMutedRef = useRef(false);
  const alertSettingsRef = useRef(DEFAULT_ALERT_SETTINGS);
  const currentOrdersRef = useRef([]);
  const lastSoundAtRef = useRef(0);
  const flashTimerRef = useRef(null);

  const [device, setDevice] = useState(null);
  const [orders, setOrders] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [alertSettings, setAlertSettings] = useState(DEFAULT_ALERT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [pairing, setPairing] = useState(Boolean(token));
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");
  const [actionId, setActionId] = useState(null);
  const [soundReady, setSoundReady] = useState(false);
  const [soundMuted, setSoundMuted] = useState(false);
  const [soundActivating, setSoundActivating] = useState(false);
  const [soundActivationError, setSoundActivationError] = useState("");
  const [flashActive, setFlashActive] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [reviewOrder, setReviewOrder] = useState(null);
  const [activeMode, setActiveMode] = useState("orders");

  const triggerVisualAlert = useCallback(() => {
    if (!alertSettingsRef.current.visual_enabled) return;
    setFlashActive(true);
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => setFlashActive(false), 7000);
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (!navigator.wakeLock || document.visibilityState !== "visible" || wakeLockRef.current) return;
    try {
      const lock = await navigator.wakeLock.request("screen");
      wakeLockRef.current = lock;
      lock.addEventListener("release", () => {
        if (wakeLockRef.current === lock) wakeLockRef.current = null;
      });
    } catch (_err) {
      // Wake Lock is optional; guided access or kiosk mode can keep older devices awake.
    }
  }, []);

  const triggerSoundAlert = useCallback(async () => {
    if (!alertSettingsRef.current.sound_enabled || !soundReadyRef.current || soundMutedRef.current) return;
    const entry = alertAudioRef.current;
    if (!entry || entry.tone !== alertSettingsRef.current.tone) {
      soundReadyRef.current = false;
      setSoundReady(false);
      setSoundActivationError("Sound settings changed. Activate and test the alert again.");
      return;
    }
    try {
      entry.audio.pause();
      entry.audio.currentTime = 0;
      entry.audio.volume = Math.max(0, Math.min(1, Number(alertSettingsRef.current.volume || 0) / 100));
      await entry.audio.play();
      lastSoundAtRef.current = Date.now();
      setSoundActivationError("");
    } catch (_err) {
      soundReadyRef.current = false;
      setSoundReady(false);
      setSoundActivationError("The browser stopped the sound channel. Activate and test it again.");
    }
  }, []);

  const completeServiceDisplayPairing = useCallback(async (pairingToken) => {
    const credential = await createDeviceCredential();
    const res = await axios.post(
      `/api/pos/device-pairing/${encodeURIComponent(pairingToken)}`,
      {
        public_key_jwk: credential.publicKeyJwk,
        expected_device_type: "service_display",
      },
      { withCredentials: true }
    );
    await completeDeviceCredential(credential, res.data?.device);
    return res;
  }, []);

  const loadOrders = useCallback(async (options = {}) => {
    try {
      if (!options.silent) setLoading(true);
      const headers = await buildDeviceAuthHeaders("GET");
      const res = await axios.get("/api/pos/service-display/orders", { headers, withCredentials: true });
      const nextOrders = res.data?.new_orders || [];
      const nextSettings = { ...DEFAULT_ALERT_SETTINGS, ...(res.data?.alert_settings || {}) };
      const nextIds = new Set(nextOrders.map((order) => Number(order.id)));
      const previousIds = knownOrderIdsRef.current;
      const incomingOrders = previousIds
        ? nextOrders.filter((order) => !previousIds.has(Number(order.id)))
        : nextOrders;

      setDevice(res.data?.device || null);
      setOrders(nextOrders);
      setRecentOrders(res.data?.recent_orders || []);
      if (nextOrders.length) setReviewOrder(null);
      setAlertSettings(nextSettings);
      const currentAudio = alertAudioRef.current;
      if (!nextSettings.sound_enabled && currentAudio) {
        disposeAlertAudio(currentAudio);
        alertAudioRef.current = null;
        soundReadyRef.current = false;
        setSoundReady(false);
        setSoundActivationError("");
      } else if (soundReadyRef.current && currentAudio?.tone !== nextSettings.tone) {
        disposeAlertAudio(currentAudio);
        alertAudioRef.current = null;
        soundReadyRef.current = false;
        setSoundReady(false);
        setSoundActivationError("Sound settings changed. Activate and test the alert again.");
      } else if (currentAudio) {
        currentAudio.audio.volume = Math.max(0, Math.min(1, Number(nextSettings.volume || 0) / 100));
      }
      alertSettingsRef.current = nextSettings;
      currentOrdersRef.current = nextOrders;
      knownOrderIdsRef.current = nextIds;
      setConnected(true);
      setError("");

      if (incomingOrders.length) {
        triggerVisualAlert();
        void triggerSoundAlert();
      }
    } catch (err) {
      setConnected(false);
      if (!options.silent) {
        setError(err?.response?.data?.error || err?.message || "This service display is not available.");
      }
    } finally {
      if (!options.silent) setLoading(false);
    }
  }, [triggerSoundAlert, triggerVisualAlert]);

  useEffect(() => {
    if (!token) {
      setPairing(false);
      loadOrders();
      return;
    }
    if (pairingStartedRef.current) return;
    pairingStartedRef.current = true;

    const pairDevice = async () => {
      try {
        setPairing(true);
        setLoading(true);
        setError("");
        const res = await completeServiceDisplayPairing(token);
        navigate(res.data?.redirect_path || "/pos/service-display", { replace: true });
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || "This pairing link is not available.");
        setLoading(false);
      } finally {
        setPairing(false);
      }
    };

    pairDevice();
  }, [completeServiceDisplayPairing, loadOrders, navigate, token]);

  useEffect(() => {
    if (token) return undefined;
    const poll = window.setInterval(() => loadOrders({ silent: true }), 3000);
    return () => window.clearInterval(poll);
  }, [loadOrders, token]);

  useEffect(() => {
    const clock = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(clock);
  }, []);

  useEffect(() => {
    const repeat = window.setInterval(() => {
      const settings = alertSettingsRef.current;
      if (!currentOrdersRef.current.length || !settings.sound_enabled || !settings.repeat_seconds) return;
      if (Date.now() - lastSoundAtRef.current < settings.repeat_seconds * 1000) return;
      void triggerSoundAlert();
    }, 1000);
    return () => window.clearInterval(repeat);
  }, [triggerSoundAlert]);

  useEffect(() => {
    document.title = orders.length ? `(${orders.length}) New orders` : "Service orders";
    return () => { document.title = "Pilcore"; };
  }, [orders.length]);

  useEffect(() => {
    const restoreTerminal = () => {
      if (document.visibilityState !== "visible") return;
      void requestWakeLock();
      if (soundReadyRef.current && currentOrdersRef.current.length) {
        void triggerSoundAlert();
      }
    };
    document.addEventListener("visibilitychange", restoreTerminal);
    window.addEventListener("focus", restoreTerminal);
    window.addEventListener("pageshow", restoreTerminal);
    return () => {
      document.removeEventListener("visibilitychange", restoreTerminal);
      window.removeEventListener("focus", restoreTerminal);
      window.removeEventListener("pageshow", restoreTerminal);
    };
  }, [requestWakeLock, triggerSoundAlert]);

  useEffect(() => () => {
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    disposeAlertAudio(alertAudioRef.current);
    alertAudioRef.current = null;
    if (wakeLockRef.current) wakeLockRef.current.release().catch(() => {});
  }, []);

  const armSound = async () => {
    if (soundActivating) return;
    try {
      setSoundActivating(true);
      setSoundActivationError("");
      const tone = alertSettingsRef.current.tone || "chime";
      if (!alertAudioRef.current || alertAudioRef.current.tone !== tone) {
        disposeAlertAudio(alertAudioRef.current);
        alertAudioRef.current = createAlertAudio(tone);
      }
      const entry = alertAudioRef.current;
      entry.audio.pause();
      entry.audio.currentTime = 0;
      entry.audio.volume = Math.max(0, Math.min(1, Number(alertSettingsRef.current.volume || 0) / 100));
      const playResult = entry.audio.play();
      if (playResult) {
        await Promise.race([
          playResult,
          new Promise((_, reject) => window.setTimeout(
            () => reject(new Error("The browser did not start the test sound. Check the phone volume and tap again.")),
            3000
          )),
        ]);
      }
      soundReadyRef.current = true;
      setSoundReady(true);
      setSoundMuted(false);
      soundMutedRef.current = false;
      lastSoundAtRef.current = Date.now();
      void requestWakeLock();
    } catch (err) {
      soundReadyRef.current = false;
      setSoundReady(false);
      const wasBlocked = err?.name === "NotAllowedError" || /interact|gesture|not allowed/i.test(String(err?.message || ""));
      setSoundActivationError(wasBlocked
        ? "The browser blocked sound. Tap the button directly on this device and try again."
        : err?.message || "Could not enable sound alerts.");
    } finally {
      setSoundActivating(false);
    }
  };

  const toggleSound = () => {
    if (!soundReady) {
      armSound();
      return;
    }
    const nextMuted = !soundMuted;
    setSoundMuted(nextMuted);
    soundMutedRef.current = nextMuted;
    if (nextMuted) alertAudioRef.current?.audio.pause();
    else void triggerSoundAlert();
  };

  const receiveOrder = async (order) => {
    try {
      setActionId(order.id);
      setError("");
      const payload = { status: "received" };
      const headers = await buildDeviceAuthHeaders("PATCH", payload);
      await axios.patch(`/api/pos/service-display/orders/${order.id}`, payload, { headers, withCredentials: true });
      await loadOrders({ silent: true });
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update the order.");
    } finally {
      setActionId(null);
    }
  };

  const enterFullscreen = () => {
    if (document.documentElement.requestFullscreen) document.documentElement.requestFullscreen().catch(() => {});
  };

  const outletLabel = useMemo(() => {
    if (!device) return "";
    if (device.receives_all_outlets) return "All outlets";
    return (device.outlets || []).map((outlet) => outlet.name).join(" · ") || "All outlets";
  }, [device]);
  const soundButtonLabel = !soundReady
    ? "Activate sound alerts"
    : soundMuted
      ? "Unmute alerts"
      : "Mute alerts";

  const pairInstalledDisplay = useCallback(async (pairingToken) => {
    try {
      setPairing(true);
      setError("");
      const res = await completeServiceDisplayPairing(pairingToken);
      navigate(res.data?.redirect_path || "/pos/service-display", { replace: true });
      await loadOrders();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Could not pair this Service Display.";
      setError(message);
      throw err;
    } finally {
      setPairing(false);
    }
  }, [completeServiceDisplayPairing, loadOrders, navigate]);

  const clearPairingError = useCallback(() => setError(""), []);

  if (loading) {
    return <div className="serviceDisplayPage"><div className="serviceDisplayState">{pairing ? "Pairing display..." : "Loading orders..."}</div></div>;
  }

  if (!device) {
    return (
      <ServiceDisplayPairing
        initialError={error}
        onClearError={clearPairingError}
        onPair={pairInstalledDisplay}
      />
    );
  }

  return (
    <div className={`serviceDisplayPage ${alertSettings.visual_enabled && orders.length ? "serviceDisplayPage--pending" : ""} ${flashActive ? "serviceDisplayPage--flash" : ""}`}>
      <header className="serviceDisplayHeader">
        <div className="serviceDisplayIdentity">
          <span className={`serviceDisplayConnection ${connected ? "serviceDisplayConnection--online" : ""}`} />
          <div>
            <h1>Service orders</h1>
            <span>{device?.name || "Service display"} · {outletLabel}</span>
          </div>
        </div>
        <div className="serviceDisplayHeaderActions">
          <span className="serviceDisplayClock"><FiClock /> {formatTime(now)}</span>
          {alertSettings.sound_enabled && soundReady && !soundMuted ? (
            <button type="button" className="serviceDisplayIconButton" onClick={() => void triggerSoundAlert()} aria-label="Test sound" title="Test sound">
              <FiBell />
            </button>
          ) : null}
          {alertSettings.sound_enabled ? (
            <button type="button" className="serviceDisplayIconButton" onClick={toggleSound} aria-label={soundButtonLabel} title={soundButtonLabel}>
              {soundMuted ? <FiVolumeX /> : <FiVolume2 />}
            </button>
          ) : null}
          <button type="button" className="serviceDisplayIconButton" onClick={enterFullscreen} aria-label="Enter fullscreen" title="Fullscreen">
            <FiMaximize />
          </button>
        </div>
      </header>

      {alertSettings.sound_enabled && !soundReady ? (
        <div className="serviceDisplaySoundGate" role="dialog" aria-modal="true" aria-labelledby="service-display-sound-title">
          <div className="serviceDisplaySoundGatePanel">
            <span className="serviceDisplaySoundGateIcon"><FiVolume2 /></span>
            <h2 id="service-display-sound-title">Activate sound alerts</h2>
            <p>Tap once and confirm that you hear the test tone.</p>
            <button type="button" onClick={armSound} disabled={soundActivating}>
              <FiVolume2 /> {soundActivating ? "Starting sound" : "Activate and test sound"}
            </button>
            {soundActivationError ? (
              <div className="serviceDisplaySoundGateError" role="alert"><FiX /> {soundActivationError}</div>
            ) : null}
            <small>Keep this screen open with the phone volume turned up.</small>
          </div>
        </div>
      ) : null}

      {error ? <div className="serviceDisplayError"><FiX /> {error}</div> : null}

      <nav className="serviceDisplayModes" aria-label="Terminal mode">
        <button type="button" className={activeMode === "orders" ? "serviceDisplayMode--active" : ""} onClick={() => setActiveMode("orders")}>
          <FiClipboard /> Orders {orders.length ? <span>{orders.length}</span> : null}
        </button>
        <button type="button" className={activeMode === "credit" ? "serviceDisplayMode--active" : ""} onClick={() => setActiveMode("credit")}>
          <FiCreditCard /> Scan credit
        </button>
      </nav>

      {activeMode === "credit" ? <ServiceDisplayCredit pendingOrderCount={orders.length} /> : <>
      <section className="serviceDisplayQueueHeader">
        <div>
          <span className="serviceDisplayQueueCount">{orders.length}</span>
          <div>
            <strong>New orders</strong>
            <span>Updated automatically</span>
          </div>
        </div>
        {alertSettings.visual_enabled && orders.length ? <span className="serviceDisplayPendingSignal"><FiBell /> Waiting for receipt</span> : null}
      </section>

      {orders.length ? (
        <main className="serviceDisplayOrderGrid">
          {orders.map((order) => (
            <article key={order.id} className="serviceDisplayOrderCard">
              <header>
                <div>
                  <strong>{order.order_number}</strong>
                  <span>{[order.outlet_name || order.device?.outlet_name || "Outlet", order.table_name].filter(Boolean).join(" · ")}</span>
                </div>
                <div className="serviceDisplayOrderAge">
                  <strong>{elapsedLabel(order.submitted_at, now)}</strong>
                  <span>{formatTime(order.submitted_at)}</span>
                </div>
              </header>
              <div className="serviceDisplayGuest">
                <strong>{order.customer_name || (order.room_number ? `Room ${order.room_number}` : "Guest")}</strong>
                <span>{orderPaymentLabel(order)}</span>
              </div>
              <div className="serviceDisplayLines">
                {(order.lines || []).map((line) => (
                  <div key={line.id}>
                    <strong>{Number(line.quantity || 0)} ×</strong>
                    <span>{line.item_name}</span>
                  </div>
                ))}
              </div>
              {order.order_note ? <div className="serviceDisplayNote">{order.order_note}</div> : null}
              <footer>
                <strong>{formatMoney(order.total_amount)}</strong>
                <button type="button" onClick={() => receiveOrder(order)} disabled={actionId === order.id}>
                  <FiCheck /> {actionId === order.id ? "Receiving" : "Received"}
                </button>
              </footer>
            </article>
          ))}
        </main>
      ) : (
        <div className="serviceDisplayEmpty">
          <FiCheckCircle />
          <strong>All orders received</strong>
          <span>New orders will appear automatically.</span>
        </div>
      )}

      {!orders.length && recentOrders.length ? (
        <section className="serviceDisplayRecent">
          <header>
            <strong>Recently received</strong>
            <span>Last 2 hours</span>
          </header>
          <div>
            {recentOrders.map((order) => (
              <div key={order.id} className="serviceDisplayRecentRow">
                <span><FiCheckCircle /> {order.order_number}</span>
                <strong>{order.customer_name || order.outlet_name || "Guest"}</strong>
                <span>{formatTime(order.received_at)}</span>
                <button type="button" onClick={() => setReviewOrder(order)} title="View order">
                  <FiEye /> View
                </button>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {reviewOrder && !orders.length ? (
        <div className="serviceDisplayHistoryOverlay" role="presentation">
          <section className="serviceDisplayHistoryModal" role="dialog" aria-modal="true" aria-labelledby="service-display-history-title">
            <header>
              <div>
                <span>Received order</span>
                <h2 id="service-display-history-title">{reviewOrder.order_number}</h2>
                <small>{[reviewOrder.outlet_name || reviewOrder.device?.outlet_name || "Outlet", reviewOrder.table_name].filter(Boolean).join(" · ")}</small>
              </div>
              <button type="button" onClick={() => setReviewOrder(null)} aria-label="Close order details" title="Close">
                <FiX />
              </button>
            </header>
            <div className="serviceDisplayHistoryBody">
              <div className="serviceDisplayGuest">
                <strong>{reviewOrder.customer_name || (reviewOrder.room_number ? `Room ${reviewOrder.room_number}` : "Guest")}</strong>
                <span>{orderPaymentLabel(reviewOrder)}</span>
              </div>
              <div className="serviceDisplayLines">
                {(reviewOrder.lines || []).map((line) => (
                  <div key={line.id}>
                    <strong>{Number(line.quantity || 0)} ×</strong>
                    <span>{line.item_name}</span>
                  </div>
                ))}
              </div>
              {reviewOrder.order_note ? <div className="serviceDisplayNote">{reviewOrder.order_note}</div> : null}
            </div>
            <footer>
              <span><FiCheckCircle /> Received at {formatTime(reviewOrder.received_at)}</span>
              <strong>{formatMoney(reviewOrder.total_amount)}</strong>
            </footer>
          </section>
        </div>
      ) : null}
      </>}
    </div>
  );
}
